import { useContext, useMemo, memo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChatContextType } from '@trinity/chat';
import { useLocation } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { useActiveApplicationIdentifier } from '@trinity/chat-components/containers/UserApplications';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  ActiveChatSessionContext,
  ChatLandingCardsVisibleContext,
  ChatLandingCardsContext,
  ChatLandingCardsPromptsContext,
  ChatLandingCardsLoadingContext,
  ChatLandingCardsUseNewExperienceContext,
  ChatSessionMessageQueriesContext,
} from './context';

type Props = {
  children: React.ReactNode;
};

const LandingCardsContainerImpl = memo((props: Props & { basePath: string }) => {
  const brandIdentifier = useActiveBrandIdentifier();
  const applicationIdentifier = useActiveApplicationIdentifier();
  const landingCardsVisibleCtx = useContext(ChatLandingCardsVisibleContext);
  const chat = useContext(ActiveChatSessionContext);
  const messageQueries = useContext(ChatSessionMessageQueriesContext);
  const apiClient = useChatApiClient();
  const basePath = props.basePath;

  const showLandingCards = useMemo(() => {
    return (() => {
      if (messageQueries.length) {
        return false;
      }

      // Don't show landing cards when the chat is embedded inside a scoped
      // surface: workspaces, projects, segments, or the segmentation builder
      // (SegmentProfile's Synthetic Audience tab lives at /segmentation/segment/:id).
      if (
        basePath.includes('/workspaces') ||
        basePath.includes('/projects') ||
        basePath.includes('/project') ||
        basePath.includes('/segments') ||
        basePath.includes('/segmentation')
      ) {
        return false;
      }

      // Don't show landing cards if chat context type is Document
      if (chat.context?.type === ChatContextType.Document) {
        return false;
      }

      return !!landingCardsVisibleCtx;
    })();
  }, [landingCardsVisibleCtx, basePath, chat.context?.type, messageQueries.length]);

  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  const query = useQuery({
    queryKey: ['landing-cards', { showLandingCards }],
    enabled: !!brandIdentifier && !!applicationIdentifier && showLandingCards,
    queryFn: async () => {
      if (!brandIdentifier || !applicationIdentifier) {
        return {
          cards: [],
          prompts: [],
          useNewExperience: true,
        };
      }
      const response = await apiClient.chat.getChatLandingCards();
      return {
        cards: response.cards,
        prompts: response.prompts,
        useNewExperience: response.useNewExperience,
      };
    },
    placeholderData: {
      cards: [],
      prompts: [],
      useNewExperience: true,
    },
  });

  return (
    <ChatLandingCardsLoadingContext.Provider value={query.isLoading}>
      <ChatLandingCardsContext.Provider value={query.data?.cards ?? []}>
        <ChatLandingCardsVisibleContext.Provider
          value={showLandingCards && !!query.data?.cards && query.data.cards.length > 0}
        >
          <ChatLandingCardsPromptsContext.Provider value={query.data?.prompts ?? []}>
            <ChatLandingCardsUseNewExperienceContext.Provider value={query.data?.useNewExperience ?? true}>
              {props.children}
            </ChatLandingCardsUseNewExperienceContext.Provider>
          </ChatLandingCardsPromptsContext.Provider>
        </ChatLandingCardsVisibleContext.Provider>
      </ChatLandingCardsContext.Provider>
    </ChatLandingCardsLoadingContext.Provider>
  );
}, (prev, next) => {
  // Prevent re-renders if basePath and children haven't changed (tab-only navigation)
  return prev.children === next.children && prev.basePath === next.basePath;
});

// Wrapper component that handles location changes
export const LandingCardsContainer = (props: Props) => {
  const location = useLocation();

  const basePath = useMemo(() => {
    const parts = location.pathname.split('/').filter(Boolean);
    const knownTabs = ['preview', 'summary', 'details', 'references'];
    if (knownTabs.includes(parts[parts.length - 1])) {
      parts.pop();
    }
    return `/${parts.join('/')}`;
  }, [location.pathname]);

  return <LandingCardsContainerImpl {...props} basePath={basePath} />;
};
